import type { rating } from '@repo/customer-api';
import {
  BASE_MOCK_CONFIG,
  type BaseMockConfig,
  knownIds,
  parseMockConfig as parseBaseConfig,
} from '@repo/utils/devTools';

/** Operations the mock customer can have; the mock credits list holds the picked ones, in this order. */
export const CREDIT_CASES = [
  { id: 'new', group: 'Al día', label: 'Recién otorgada', description: 'Compra sin cuotas vencidas ni pagos.' },
  { id: 'onTrack', group: 'Al día', label: 'A mitad de plan', description: 'Préstamo 12 cuotas, 5 pagadas.' },
  { id: 'lastInstallment', group: 'Al día', label: 'Última cuota', description: '5 de 6 cuotas pagadas.' },
  { id: 'debitCard', group: 'Al día', label: 'Débito automático', description: 'Cobro por tarjeta de débito.' },
  { id: 'qr', group: 'Al día', label: 'Compra con QR', description: 'Compra en comercio con QR interoperable.' },
  { id: 'oneOverdue', group: 'Deuda', label: '1 cuota vencida', description: 'Con atraso, con punitorios.' },
  {
    id: 'manyOverdue',
    group: 'Deuda',
    label: 'Varias cuotas vencidas',
    description: 'Con atraso, 4 cuotas vencidas con punitorios.',
  },
  {
    id: 'allOverdue',
    group: 'Deuda',
    label: 'Plan entero vencido',
    description: 'Ninguna cuota pagada y todas vencidas.',
  },
  {
    id: 'overduePartial',
    group: 'Pagos parciales',
    label: 'Parciales sobre cuota vencida',
    description: 'Caso PREVISORA: pagos de $ 17,35 y $ 22,67 que dejan saldo.',
  },
  {
    id: 'prepaid',
    group: 'Pagos parciales',
    label: 'Adelanto parcial',
    description: 'Al día, con un pago parcial a la próxima cuota antes del vencimiento.',
  },
  {
    id: 'splitInstallments',
    group: 'Pagos parciales',
    label: 'Cada cuota en 2 pagos',
    description: 'Todas las cuotas pagadas en dos partes.',
  },
  {
    id: 'manySmall',
    group: 'Pagos parciales',
    label: 'Muchos pagos chicos',
    description: '10 pagos de $ 1.000 en la misma cuota.',
  },
  {
    id: 'tinyRemainder',
    group: 'Pagos parciales',
    label: 'Saldo de centavos',
    description: 'Cuota con "Resta $ 0,01".',
  },
  { id: 'paid', group: 'Cerradas', label: 'Pagada', description: 'EstadoPago "Finalizado", se ve como "Pagado".' },
  {
    id: 'precancelled',
    group: 'Cerradas',
    label: 'Precancelada',
    description: 'Las últimas cuotas pagadas juntas el mismo día.',
  },
  { id: 'annulled', group: 'Cerradas', label: 'Anulada', description: 'Cuotas "Anulada", sin pagos.' },
  {
    id: 'longName',
    group: 'Casos borde',
    label: 'Nombre e importes largos',
    description: 'Comercio de 80 caracteres, cuotas de $ 9.999.999,99.',
  },
  { id: 'bigPlan', group: 'Casos borde', label: 'Plan de 36 cuotas', description: 'Tablas largas.' },
  {
    id: 'unknownStatus',
    group: 'Casos borde',
    label: 'Estado desconocido',
    description: 'EstadoPago inventado ("EstadoInexistente") que la app no conoce.',
  },
  {
    id: 'missingData',
    group: 'Casos borde',
    label: 'Datos incompletos',
    description: 'Sin cuotas, sin pagos, sin número ni próximo vencimiento.',
  },
] as const;

export type CreditCaseId = (typeof CREDIT_CASES)[number]['id'];

const DEBT: CreditCaseId[] = ['oneOverdue', 'manyOverdue', 'allOverdue', 'overduePartial'];

export const CREDIT_PRESETS: { id: string; label: string; cases: CreditCaseId[]; mora?: boolean }[] = [
  { id: 'all', label: 'Todo', cases: CREDIT_CASES.map(({ id }) => id) },
  { id: 'debt', label: 'Con deuda', cases: DEBT },
  {
    id: 'partial',
    label: 'Pagos parciales',
    cases: ['overduePartial', 'prepaid', 'splitInstallments', 'manySmall', 'tinyRemainder'],
  },
  { id: 'onTrack', label: 'Al día', cases: ['new', 'onTrack', 'lastInstallment', 'debitCard', 'qr'] },
  { id: 'closed', label: 'Todo pagado', cases: ['paid', 'precancelled', 'annulled'] },
  { id: 'edge', label: 'Casos borde', cases: ['longName', 'bigPlan', 'unknownStatus', 'missingData'] },
  { id: 'blocked', label: 'Bloqueado por mora', cases: DEBT, mora: true },
  { id: 'empty', label: 'Sin operaciones', cases: [] },
];

export type MockConfig = BaseMockConfig & {
  cases: CreditCaseId[];
  // The credits response's global Mora flag: Operaciones and Pagos show only the default warning
  mora: boolean;
};

export const DEFAULT_MOCK_CONFIG: MockConfig = { ...BASE_MOCK_CONFIG, cases: CREDIT_PRESETS[0]!.cases, mora: false };

// A cookie from an older version may name cases that no longer exist
export const parseMockConfig = (value?: string | null): MockConfig =>
  parseBaseConfig(value, DEFAULT_MOCK_CONFIG, (config) => ({ ...config, cases: knownIds(CREDIT_CASES, config.cases) }));

// The offers carousel reads the "offers" cookie the login used to write, so the panel writes it directly
export const OFFERS_SCENARIOS: { id: string; label: string; ratings: rating[] }[] = [
  { id: 'none', label: 'Sin ofertas', ratings: [] },
  {
    id: 'both',
    label: 'Compra y efectivo',
    ratings: [
      { status: 'APPROVED', maxAmount: 495000, maxTerm: 4, offerType: 'BNPL', renewalDate: '15/11/2026' },
      { status: 'APPROVED', maxAmount: 225000, maxTerm: 12, offerType: 'EFECTIVO', renewalDate: '15/11/2026' },
    ],
  },
  {
    id: 'many',
    label: 'Cinco ofertas',
    ratings: [
      { status: 'APPROVED', maxAmount: 1500000, maxTerm: 18, offerType: 'EFECTIVO', renewalDate: '15/11/2026' },
      { status: 'APPROVED', maxAmount: 9999999, maxTerm: 24, offerType: 'BNPL', renewalDate: '15/11/2026' },
      { status: 'APPROVED', maxAmount: 50000, maxTerm: 3, offerType: 'BNPL', renewalDate: '15/11/2026' },
      { status: 'APPROVED', maxAmount: 225000, maxTerm: 12, offerType: 'EFECTIVO', renewalDate: '15/11/2026' },
      { status: 'APPROVED', maxAmount: 495000, maxTerm: 4, offerType: 'BNPL', renewalDate: '15/11/2026' },
    ],
  },
];
